import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

// The consumer supplies the committed order and owns the sort cycle and data
// fetch. The quiet form (reveal) hugs its label and glyph, truncates the
// label, and shows an unsorted glyph only on hover or keyboard focus.
export function SortHeader({
  children,
  sort = 'none',
  onSort,
  failureLabel,
  onFailure,
  reveal = false,
  inset = false,
  labelRef,
  wrapButton,
  after,
  ...props
}) {
  const [state, setState] = useState({ pending: false })
  const action = useRef(null)
  const button = useRef(null)
  const failure = useRef(onFailure)
  failure.current = onFailure
  useEffect(() => {
    const { announce, createActionState } = window.MEButtons
    action.current = createActionState(setState, announce, error =>
      failure.current?.(error)
    )
    return () => action.current.dispose()
  }, [])
  const control = (
    <button
      ref={button}
      type="button"
      className={[
        'me-th-sort',
        reveal && 'me-th-sort-reveal',
        reveal && inset && 'me-th-sort-inset',
      ]
        .filter(Boolean)
        .join(' ')}
      data-sort=""
      data-me-react=""
      aria-disabled={state.pending || undefined}
      aria-busy={state.phase === 'busy' || undefined}
      onClick={() => {
        if (action.current.pending) return
        // Read first: onSort may unmount the header.
        const label = button.current.textContent.trim()
        let request
        try {
          request = onSort()
        } catch (error) {
          request = Promise.reject(error)
        }
        action.current.run(request, {
          load: 'none',
          busyLabel: window.MEButtons.text('strSorting', { label }),
          failureLabel,
        })
      }}
    >
      {reveal ? (
        <span ref={labelRef} className="me-th-sort-label">
          {children}
        </span>
      ) : (
        children
      )}
      <span className="me-th-sort-glyph">
        <i
          className={window.MEButtons.sortGlyphClass(sort, reveal)}
          aria-hidden="true"
        />
      </span>
    </button>
  )
  return (
    <th {...props} aria-sort={sort}>
      {wrapButton ? wrapButton(control) : control}
      {after}
    </th>
  )
}

SortHeader.propTypes = {
  children: PropTypes.node.isRequired,
  sort: PropTypes.oneOf(['none', 'ascending', 'descending']),
  // Requests the next order and returns the request's promise.
  onSort: PropTypes.func.isRequired,
  failureLabel: PropTypes.string.isRequired,
  onFailure: PropTypes.func,
  // The quiet form: the sort triangles, a fill that hugs the label and glyph,
  // and an unsorted glyph that shows only on hover or keyboard focus.
  reveal: PropTypes.bool,
  // With reveal: the control draws its focus ring inside its box and pads at
  // the xs control's vertical padding on every side, so its ring and fill stay
  // clear of another control at the cell's edge (a column resize boundary).
  inset: PropTypes.bool,
  // Receives the quiet form's label element, for measuring truncation.
  labelRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  // Wraps the sort button, as a tooltip trigger does; returns the node the
  // cell renders in its place.
  wrapButton: PropTypes.func,
  // Cell content after the sort button, such as a column resize handle.
  after: PropTypes.node,
}
